/**
 * utilizationFromCalendar.js — how busy the team is, computed from the Manpower
 * calendar instead of the utilization workbook.
 *
 * The workbook was a monthly export somebody had to remember to refresh; the
 * calendar is what people actually keep current, so the chart now follows the
 * same rows the Engineers tab draws (manpower_edits).
 *
 * The rules, all asked for explicitly:
 *   WINDOW   five months — two back, the current one, two forward. Forward
 *            months are real numbers here, because the calendar holds future
 *            assignments; a timesheet export never could.
 *   PTO      excluded from the utilisation PERCENTAGE on both sides - a week
 *            off is not idle capacity that went unsold - but reported as its
 *            own series, because "where did the time go" is the real question.
 *            A day is PTO when its label says so or its block was scheduled
 *            with Status PTO (since r49 the calendar keeps Status per day).
 *   WEEKDAYS Mon-Fri only. Weekends are not capacity.
 *   SPLIT    booked days divide into SITE and OFFICE, so the chart shows where
 *            the time went and not only how much of it was sold.
 *   INTERNAL only internal engineers count (data/resourceGroups.js: NA or no
 *            region, employee) - never interns, contractors, South Africa,
 *            MENA or Europe (Sharif, 10/16/26; it was "NA only").
 *   INTERNS  can be scheduled like anyone, but are left out of every number
 *            here - they are not sellable capacity (Sharif, 10/2/26).
 *   TABLES   only engineers drawn in a calendar table whose "Counts toward
 *            utilization" switch is ON count (10/16/26: "only the process and
 *            controls calendars affect the utilization"). Controls and Process
 *            start ON, every other table OFF; `layout` is GET
 *            /api/manpower/tables (components/manpower/calendarTables.js) and
 *            without one it is today's default, both tables counting.
 *
 * Pure: no fetching, no clock of its own (pass `today`). Tested in
 * src/utils/__tests__/utilizationFromCalendar.test.js.
 */

import { DEFAULT_LAYOUT, countsTowardUtilization, isIntern } from '../components/manpower/calendarTables';
import { localDay } from './localDay';

/** Labels that mean "not working", not "not sold". */
const OFF_LABEL = /\b(pto|vacation|holiday|leave|sick|off)\b/i;

export const isTimeOff = (label) => !!label && OFF_LABEL.test(String(label));

/** A calendar block's Status (On-Site / Office / PTO) as 'site' | 'office' | 'pto', or ''. */
const statusKind = (status) => {
  const st = String(status || '')
    .trim()
    .toLowerCase();
  if (st === 'pto') return 'pto';
  if (st === 'on-site' || st === 'onsite') return 'site';
  if (st === 'office') return 'office';
  return '';
};

/** A day off: the label says so, or the block was scheduled with Status PTO. */
export const isPtoDay = (label, detail) => isTimeOff(label) || statusKind(detail && detail.status) === 'pto';

/**
 * How each engineer usually works: the more common of On-Site / Office among
 * the Statuses on THEIR OWN booked calendar days. { name: 'site' | 'office' };
 * a tie, or nobody's Status set, leaves the name out.
 *
 * This replaced the card status as workKind's fallback: since r49 the
 * calendar rewrites the card status every day, so one PTO day on the card
 * would have filed every unmarked booked day in all five months as Office.
 * The calendar's own history does not flip with today.
 */
export function usualModes(edits, details) {
  const map = edits || {};
  const counts = {};
  for (const [key, d] of Object.entries(details || {})) {
    if (!map[key] || isTimeOff(map[key])) continue; // only booked work days; a detail on an unbooked day is stale
    const kind = statusKind(d && d.status);
    if (kind !== 'site' && kind !== 'office') continue;
    const name = key.slice(0, key.lastIndexOf('|'));
    const c = counts[name] || (counts[name] = { site: 0, office: 0 });
    c[kind] += 1;
  }
  const out = {};
  for (const [name, c] of Object.entries(counts)) {
    if (c.site !== c.office) out[name] = c.site > c.office ? 'site' : 'office';
  }
  return out;
}

/**
 * Site or office, for one booked day.
 *
 * Since r49 a calendar block carries its own Status, picked when it was
 * scheduled - that is somebody saying outright where this day is spent, so it
 * wins. Next comes the convention the team already writes into the label -
 * "Oshkosh -Site", "GM P15 - Offline", "Lucid - Offline". Most labels carry no
 * marker, so next is how this engineer usually works on the calendar (`usual`,
 * from usualModes), then the card status (engineers.status On-Site / Office)
 * unless it is PTO - the card now follows TODAY's calendar, so PTO there says
 * nothing about other days - and last 'office'.
 *
 * Those fallbacks are DEFAULTS, not measurements. Mark the block or the label
 * and it wins.
 */
export function workKind(label, engineer, detail, usual) {
  const fromStatus = statusKind(detail && detail.status);
  if (fromStatus) return fromStatus;
  const l = String(label || '');
  if (isTimeOff(l)) return 'pto';
  // Word-bounded: an unanchored /home/ would file "Homestead" as office.
  if (/\boff-?line\b|\boffice\b|\bhome\b|\bremote\b/i.test(l)) return 'office';
  if (/\bon-?site\b|\bsite\b|\bfield\b/i.test(l)) return 'site';
  if (usual === 'site' || usual === 'office') return usual;
  if (statusKind(engineer && engineer.status) === 'site') return 'site';
  return 'office';
}

/** Employment type Intern, however it was typed. */
export { isIntern };

/**
 * Who counts toward capacity: active internal engineers (not interns,
 * contractors, South Africa, MENA or Europe) who sit in a calendar table that
 * counts toward utilization (`layout`) - see countsTowardUtilization.
 *
 * The card's status used to filter here too (PTO on the card = out). Since r49
 * the calendar writes that status day by day, so one PTO day would have taken
 * someone out of all five months. PTO is counted per day from the calendar
 * instead (monthlyUtilization, availableHours).
 */
export function utilizationRoster(engineers, layout = DEFAULT_LAYOUT) {
  return (engineers || []).filter(
    (e) => e && e.name && !(e.isArchived || e.is_archived) && countsTowardUtilization(e, layout || DEFAULT_LAYOUT)
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Mon-Fri days in a month, as YYYY-MM-DD. Weekends are not capacity. */
export function weekdaysOf(year, monthIndex) {
  const out = [];
  const d = new Date(year, monthIndex, 1);
  while (d.getMonth() === monthIndex) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) out.push(localDay(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** A working day is 8 hours - GLOBAL's rule is per day, not 40 a week. */
export const HOURS_PER_DAY = 8;

/**
 * Engineer-hours the team can actually deliver in one month: for each person
 * on the utilization roster, (weekdays in the month - their PTO weekdays) x 8.
 * Weekends, PTO and interns are all out - the same rules as the utilization
 * chart, so the forecast's Available line and that chart agree.
 *
 * @returns { hours, headcount, weekdays, ptoDays }
 */
export function availableHours({ engineers, edits, details, layout, year, monthIndex, hoursPerDay = HOURS_PER_DAY }) {
  const roster = utilizationRoster(engineers, layout);
  const map = edits || {};
  const info = details || {};
  const days = weekdaysOf(year, monthIndex);
  let ptoDays = 0;
  for (const e of roster) {
    for (const day of days) {
      const key = `${e.name}|${day}`;
      if (isPtoDay(map[key], info[key])) ptoDays += 1;
    }
  }
  return {
    hours: (roster.length * days.length - ptoDays) * hoursPerDay,
    headcount: roster.length,
    weekdays: days.length,
    ptoDays,
  };
}

/**
 * @param engineers  the roster (filtered here, pass them all)
 * @param edits      { 'Engineer Name|YYYY-MM-DD': 'Project label' | null }
 * @param details    { 'Engineer Name|YYYY-MM-DD': { location?, projectNumber?, status? } }
 * @param layout     the calendar tables (which count); default: Controls + Process
 * @param today      anchor for the five-month window
 * @returns [{ week, util, assigned, capacity, month, year }] oldest first
 */
export function monthlyUtilization({ engineers, edits, details, layout, today = new Date() }) {
  const roster = utilizationRoster(engineers, layout);
  const map = edits || {};
  const info = details || {};
  const usual = usualModes(map, info);

  const points = [];
  for (let offset = -2; offset <= 2; offset++) {
    const anchor = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const year = anchor.getFullYear();
    const monthIndex = anchor.getMonth();
    const days = weekdaysOf(year, monthIndex);

    let capacity = 0;
    let assigned = 0;
    let site = 0;
    let office = 0;
    let pto = 0;
    for (const e of roster) {
      for (const day of days) {
        const key = `${e.name}|${day}`;
        const label = map[key];
        if (isPtoDay(label, info[key])) {
          pto += 1;
          continue;
        } // time, but not capacity
        capacity += 1;
        if (!label) continue;
        assigned += 1;
        if (workKind(label, e, info[key], usual[e.name]) === 'site') site += 1;
        else office += 1;
      }
    }
    const allDays = capacity + pto;
    const pct = (n, d) => (d ? Math.round((n / d) * 1000) / 10 : 0);
    points.push({
      week: MONTHS[monthIndex], // the chart's x key is named `week`
      month: monthIndex + 1,
      year,
      assigned,
      capacity,
      siteDays: site,
      officeDays: office,
      ptoDays: pto,
      util: pct(assigned, capacity),
      site: pct(site, capacity),
      office: pct(office, capacity),
      // PTO is a share of ALL days, not of sellable capacity - those days are
      // real time that went somewhere, they just are not capacity.
      pto: pct(pto, allDays),
      isCurrent: offset === 0,
    });
  }
  return points;
}
